import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { VALIDATION_TYPES } from "@/components/Formik";
import { Flex, Text } from "@/eworkitComponents";

import { PasswordValidationField } from "./PasswordValidationField";

export enum PASSWORD_VALIDATION_TYPES {
  LENGTH = "PASSWORD_LENGTH",
  STRONG_ENOUGH = "PASSWORD_STRONG_ENOUGH",
}

const passwordCharTypeRegExps: RegExp[] = [/[a-z]/, /[A-Z]/, /[0-9]/, /[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/];

export const passwordValidationSchema = Yup.string()
  .required(VALIDATION_TYPES.REQUIRED)
  .min(8, PASSWORD_VALIDATION_TYPES.LENGTH)
  .test("is-strong-enough", PASSWORD_VALIDATION_TYPES.STRONG_ENOUGH, (v: string) => {
    if (!v) return false;
    return passwordCharTypeRegExps.reduce((prev, curr) => prev + (v.match(curr) ? 1 : 0), 0) >= 2;
  });

export interface IPasswordValidationProps {
  errors: Set<unknown>;
}

export const PasswordValidation: FC<IPasswordValidationProps> = memo(props => {
  const { t } = useTranslation(["common", "public"]);

  const lengthError = props.errors.has(PASSWORD_VALIDATION_TYPES.LENGTH);
  const charTypesError = props.errors.has(PASSWORD_VALIDATION_TYPES.STRONG_ENOUGH);

  return (
    <Flex direction="column">
      <Text size="sm">{t("public:COMPONENTS.PASSWORD_VALIDATION_LABEL")}</Text>
      <PasswordValidationField
        invalid={lengthError}
        text={t(`VALIDATION_MESSAGES.${PASSWORD_VALIDATION_TYPES.LENGTH}`)}
        showIcon
      />
      <PasswordValidationField
        invalid={charTypesError}
        text={t("VALIDATION_MESSAGES.PASSWORD_STRONG_ENOUGH_INFO")}
        showIcon>
        <Flex direction="column" padding={["xs", false, false, "md"]} innerMargin="xs">
          <PasswordValidationField invalid={charTypesError} text={t("VALIDATION_MESSAGES.PASSWORD_UPPERCASE_LETTER")} />
          <PasswordValidationField invalid={charTypesError} text={t("VALIDATION_MESSAGES.PASSWORD_LOWERCASE_LETTER")} />
          <PasswordValidationField invalid={charTypesError} text={t("VALIDATION_MESSAGES.PASSWORD_DIGIT")} />
          <PasswordValidationField
            invalid={charTypesError}
            text={t("VALIDATION_MESSAGES.PASSWORD_SPECIAL_CHARACTER")}
          />
        </Flex>
      </PasswordValidationField>
    </Flex>
  );
});
